import { chakra, Flex, Text } from "@chakra-ui/react";

/** Key for a reference line (average, benchmark, target). */
export type LineLegendItem = {
  label: string;
  color: string;
  strokeDasharray?: string;
  strokeWidth?: number;
  /** Draw a filled square (series colour) instead of a line. */
  swatch?: boolean;
  opacity?: number;
};

export const LINE_LEGEND_SWATCH_WIDTH = 28;

export const LegendSwatch = ({
  color,
  strokeDasharray,
  strokeWidth,
  swatch,
  opacity,
}: Omit<LineLegendItem, "label">) =>
  swatch ? (
    <chakra.span
      flexShrink={0}
      w="10px"
      h="10px"
      borderRadius="2px"
      bg={color}
      opacity={opacity}
      aria-hidden
    />
  ) : (
    <chakra.svg
      flexShrink={0}
      w={`${LINE_LEGEND_SWATCH_WIDTH}px`}
      h="12px"
      opacity={opacity}
      aria-hidden
    >
      <line
        x1={0}
        x2={LINE_LEGEND_SWATCH_WIDTH}
        y1={6}
        y2={6}
        stroke={color}
        strokeWidth={strokeWidth ?? 1.5}
        strokeDasharray={strokeDasharray}
      />
    </chakra.svg>
  );

export const LineLegend = ({
  items,
  title,
  direction = "row",
}: {
  items: LineLegendItem[];
  title?: string;
  direction?: "row" | "column";
}) => (
  <Flex
    gap={direction === "row" ? 5 : 2}
    direction={direction}
    flexWrap="wrap"
    fontSize="sm"
    mt={direction === "row" ? 2 : 0}
    alignItems={direction === "row" ? "center" : "flex-start"}
  >
    {title && (
      <Text as="span" fontWeight="semibold">
        {title}
      </Text>
    )}
    {items.map(({ label, ...swatch }) => (
      <Flex key={label} alignItems="center" gap={2}>
        <LegendSwatch {...swatch} />
        <Text as="span">{label}</Text>
      </Flex>
    ))}
  </Flex>
);
